import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { BottomSheet } from '../components';
import { useAppData } from '../data';
import { cn } from '../lib/cn';
import { formatPayDate, fromIsoDate } from '../lib/date';
import { resolveIcon } from '../lib/icons';
import { springGentle } from '../tokens';

type Props = { open: boolean; onClose: () => void };

/**
 * BottomSheet listing every achievement with its lock state. Unlocked
 * rows show the icon in accent + the unlock date; locked rows are
 * dimmed with a small Lock badge in place of the icon.
 */
export function AchievementsSheet({ open, onClose }: Props) {
  const data = useAppData();
  const unlocked = data.achievements.filter((a) => !!a.unlockedAt).length;
  const total = data.achievements.length;

  return (
    <BottomSheet open={open} onClose={onClose} title="Achievements">
      <div className="px-card-pad pt-2 pb-3 text-center">
        <div className="text-[28px] font-light font-mono tabular-nums text-text-primary">
          {unlocked}
          <span className="text-text-tertiary"> / {total}</span>
        </div>
        <div className="mt-1 text-[12px] uppercase tracking-[0.05em] text-text-tertiary">
          Unlocked
        </div>
      </div>

      <div className="px-card-pad pb-card-pad flex flex-col gap-2">
        {data.achievements.map((a, i) => {
          const Icon = resolveIcon(a.icon);
          const isUnlocked = !!a.unlockedAt;
          return (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springGentle, delay: i * 0.02 }}
              className={cn(
                'flex items-center gap-3 rounded-card px-card-pad py-3',
                isUnlocked ? 'bg-surface-secondary' : 'bg-surface-secondary/60',
              )}
            >
              <div
                className={cn(
                  'w-10 h-10 rounded-button shrink-0 inline-flex items-center justify-center',
                  isUnlocked
                    ? 'bg-accent text-white shadow-sm'
                    : 'bg-text-tertiary/20 text-text-tertiary',
                )}
              >
                {isUnlocked ? (
                  <Icon size={18} strokeWidth={1.5} />
                ) : (
                  <Lock size={14} strokeWidth={1.8} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className={cn(
                    'text-[15px] font-semibold truncate',
                    isUnlocked ? 'text-text-primary' : 'text-text-tertiary',
                  )}
                >
                  {a.title}
                </div>
                <div
                  className={cn(
                    'text-[12px] truncate mt-0.5',
                    isUnlocked ? 'text-text-secondary' : 'text-text-tertiary',
                  )}
                >
                  {a.description}
                </div>
              </div>
              {isUnlocked && a.unlockedAt ? (
                <div className="text-[11px] text-text-tertiary shrink-0 text-right">
                  {formatPayDate(fromIsoDate(a.unlockedAt.slice(0, 10)))}
                </div>
              ) : null}
            </motion.div>
          );
        })}
      </div>
    </BottomSheet>
  );
}
